import { useCallback, useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark, faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import Button from "../../../../components/Button";
import { Switch } from "../../../../components/ui/switch";
import BaseForm, { Field } from "../../../../components/shared/Form";
import DateInput from "../../../../components/shared/inputs/DateInput";
import Simple from "./Simple";
import Advanced from "./Advanced";
import Breaks from "./Breaks";
import { useDefaultSection } from "./hooks/useDefaultSection";
import { useInitialValues } from "./hooks/useInitialValues";
import { useUser } from "../../../../utils/hooks/user";
import { useGroups } from "../../../Settings/General/OrganisationLevelGroups/context";
import { createEventSchema } from "../../../TraxxionSchedule/Component/components/drawers/Event/Form/zodValidation";
import { calculateFormValues } from "../../../TraxxionSchedule/Component/helpers/form/calculateFormValues";
import { getFormFormattedValues } from "../../../TraxxionSchedule/Component/helpers/form/getFormFormattedValues";
import { getSectionsBounds } from "../../../TraxxionSchedule/Component/helpers/form/sections";
import { getLevelNames, sectionsToSimpleValues, simpleValuesToSections } from "../../../TraxxionSchedule/Component/helpers/form/simpleAdvanced";
import { combineDateAndTime } from "../../../../utils/helpers/date";
import "./styles.scss";

/**
 * Flattens the server's validation errors into `[fieldPath, message]` pairs,
 * e.g. `{ sections: [{ start: ["..."] }] }` -> `[["sections.0.start", "..."]]`.
 * A field's list of messages keeps only the first one.
 */
function getServerErrors(error: unknown, path = ""): [string, string][] {
    if (typeof error === "string") {
        return path ? [[path, error]] : [];
    }
    if (Array.isArray(error) && error.every((item) => typeof item === "string")) {
        return path && error.length > 0 ? [[path, error[0]]] : [];
    }
    if (!error || typeof error !== "object") {
        return [];
    }
    return Object.entries(error).flatMap(([key, value]) =>
        getServerErrors(value, path ? `${path}.${key}` : key)
    );
}

/**
 * Schedule request form with the fields and calculations of the TraxxionSchedule event form.
 * `values.data` is the request's schedule when editing or viewing; without it a new one is created.
 * `onFinish` receives the schedule already formatted for the request payload.
 */
function ScheduleForm({ values, onClose, onFinish, error, loading, mode }) {
    const { t } = useTranslation();
    const currentUser = useUser();
    const { allocatedGroups } = useGroups();
    const data = values?.data;
    const isCreating = !data;
    const disabled = mode === "view" || mode === "timesheet-view";
    
    // The request's owner: the logged in user unless a manager is viewing someone else's request.
    const user = useMemo(() => {
        const owner = [values?.user, data?.user].find((item) => typeof item === "object" && item?.id);
        return owner?.id && owner.id !== currentUser?.id ? owner : currentUser;
    }, [values?.user, data?.user, currentUser]);
    
    const event = useMemo(() => ({ ...data, user, isCreating }), [data, user, isCreating]);
    
    const { defaultSection, breaks } = useDefaultSection({ user, date: data?.date, data });
    const initialValues = useInitialValues({ data, defaultSection, breaks });
    
    const schema = useMemo(
        () => createEventSchema({ t, intervals: [], mode: "week", selectedCellOrEvent: event, allocatedGroups }),
        [t, event, allocatedGroups]
    );
    
    const form = useForm({
        defaultValues: initialValues,
        resolver: zodResolver(schema),
    });
    const [isSimple, sections, date] = useWatch({ control: form.control, name: ["isSimple", "sections", "date"] });

    // Reload when another request is opened (the view drawer reuses the form).
    useEffect(() => {
        if (data) {
            form.reset(initialValues);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // Fill in the defaults a new shift inherits once they resolve.
    const { payCode, job, location } = initialValues;
    useEffect(() => {
        if (!isCreating) {
            return;
        }
        Object.entries({ payCode, job, location }).forEach(([name, value]) => {
            if (!form.getValues(name) && value) {
                form.setValue(name, value);
            }
        });
    }, [isCreating, payCode, job, location, form]);

    // In advanced mode the shift spans its sections.
    useEffect(() => {
        if (isSimple || !sections?.length) {
            return;
        }
        const anchor = moment(date);
        const { start, end } = getSectionsBounds(
            sections
                .filter((section) => section?.start && section?.end)
                .map((section) => {
                    const start = combineDateAndTime(anchor, moment(section.start));
                    let end = combineDateAndTime(anchor, moment(section.end));
                    if (moment(end).isBefore(start)) {
                        end = moment(end).add(1, "day");
                    }
                    return { ...section, start, end };
                })
        );
        if (start && !moment(form.getValues("startDate")).isSame(start)) {
            form.setValue("startDate", start);
        }
        if (end && !moment(form.getValues("endDate")).isSame(end)) {
            form.setValue("endDate", end);
        }
    }, [isSimple, sections, date, form]);

    const onIsSimpleChange = useCallback((nextIsSimple) => {
        const formValues = form.getValues();
        const levelNames = getLevelNames(allocatedGroups);
        if (nextIsSimple) {
            const simpleValues = sectionsToSimpleValues(formValues, levelNames);
            Object.entries(simpleValues || {}).forEach(([name, value]) => form.setValue(name, value));
            return;
        }
        const _sections = simpleValuesToSections(formValues, {
            levelNames,
            isCreating,
            calcGroupBreaks: user?.calculationGroup?.breaks,
        });
        if (_sections) {
            form.setValue("sections", _sections);
        }
    }, [form, allocatedGroups, isCreating, user?.calculationGroup?.breaks]);

    useEffect(() => {
        if (!error || typeof error !== "object") {
            return;
        }
        getServerErrors(error).forEach(([field, message], index) => {
            form.setError(field, { type: "server", message }, { shouldFocus: index === 0 });
        });
    }, [error, form]);

    const submit = useCallback((formValues) => {
        const calculated = calculateFormValues(formValues, event, defaultSection);
        onFinish?.(getFormFormattedValues(calculated));
    }, [event, defaultSection, onFinish]);

    return (
        <BaseForm form={form} onFinish={submit} className="schedule-form h-100">
            <div
                className="p-4"
                style={{
                    height: mode ? "100%" : "calc(100% - 70px)",
                    overflowY: "auto",
                    overflowX: "hidden"
                }}
            >
                <Field name="date" label={t("shift-date")} layout="row" required>
                    <DateInput disabled={disabled} />
                </Field>

                <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">
                    <Field
                        name="isSimple"
                        label={isSimple ? t("simple") : t("advanced")}
                        layout="row"
                        valuePropName="checked"
                        shouldUnregister={false}
                    >
                        <Switch onCheckedChange={onIsSimpleChange} disabled={disabled} />
                    </Field>

                    {isSimple && <Breaks user={user} disabled={disabled} />}
                </div>

                {isSimple
                    ? <Simple user={user} disabled={disabled} />
                    : <Advanced user={user} isCreating={isCreating} disabled={disabled} />}
            </div>

            {!mode && (
                <div className="border-top py-3 text-center align-items-center justify-content-center">
                    <Button
                        className="btn-icon text-muted"
                        type="button"
                        color="white"
                        onClick={onClose}
                        loading={false}
                        icon={<FontAwesomeIcon icon={faCircleXmark} />}
                    >
                        {t("cancel")}
                    </Button>

                    <Button
                        className="btn-icon text-info"
                        type="submit"
                        color="white"
                        disabled={loading}
                        loading={loading}
                        icon={<FontAwesomeIcon icon={faCircleCheck} />}
                    >
                        {t("send-for-approval")}
                    </Button>
                </div>
            )}
        </BaseForm>
    );
}

export default ScheduleForm;
